import React, { useRef, useState, useEffect } from "react"
import Table from "../../../core/common/dataTable/index"
import { Link, useParams } from "react-router-dom"
import TooltipOption from "../../../core/common/tooltipOption"
import CommonSelect from "../../../core/common/commonSelect"
import * as XLSX from "xlsx"
import { all_routes } from "../../router/all_routes"
import {
  getAllCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  Course,
  CourseCreateData,
  CourseUpdateData,
} from "../../../service/courses"
import { getAllClasses, Class } from "../../../service/classes"
import { getAllSections } from "../../../service/classes"
import { getAllUsers, USER } from "../../../service/user"
import { toast } from "react-toastify"
import { useReactToPrint } from "react-to-print"
import { CLASSES_SORT_OPTIONS } from "../../../core/common/selectoption/selectoption"
import { useSelector } from "react-redux"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"

const ClassSubject = () => {
  const routes = all_routes
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
  const componentRef = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  const classIdFromUrl = id
  const currentSession = useSelector(selectCurrentSession)
  const currentBranch = useSelector(selectCurrentBranch)

  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  })
  const [filters, setFilters] = useState({
    status: "",
    class: classIdFromUrl || "",
    section: "",
    session: "",
  })

  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [sectionOptions, setSectionOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [teacherOptions, setTeacherOptions] = useState<
    { value: string; label: string }[]
  >([])

  const [sortBy, setSortBy] = useState(CLASSES_SORT_OPTIONS[0])
  const [searchText, setSearchText] = useState("")

  const [formData, setFormData] = useState<CourseCreateData>({
    title: "",
    courseIdentifier: "",
  })
  const [editFormData, setEditFormData] = useState<CourseUpdateData>({})
  const [selectedCourseId, setSelectedCourseId] = useState<string>("")

  const [currentClassName, setCurrentClassName] = useState<string>("")

  const fetchDropdownData = async () => {
    try {
      const classesResponse = await getAllClasses({})
      const classOptionsData = classesResponse.classes.map((cls: Class) => ({
        value: cls._id,
        label: cls.name,
      }))
      setClassOptions(classOptionsData)

      if (classIdFromUrl) {
        const currentClass = classesResponse.classes.find(
          (cls: Class) => cls._id === classIdFromUrl
        )
        if (currentClass) {
          setCurrentClassName(currentClass.name)
        }
      }

      const sectionsResponse = await getAllSections({ session: currentSession?._id })
      const sectionOptionsData = sectionsResponse.sections.map(
        (section: any) => ({
          value: section._id,
          label: section.name,
        })
      )
      setSectionOptions(sectionOptionsData)

      const usersResponse = await getAllUsers({
        status: "active",
      })

      const teacherOptionsData = usersResponse.data
        .filter((user: USER) => {
          return (
            typeof user.role === "object" &&
            user.role !== null &&
            "name" in user.role &&
            user.role.name.toLowerCase() === "teacher"
          )
        })
        .map((user: USER) => {
          return {
            value: user._id,
            label: `${user.firstName} ${user.lastName}`,
          }
        })

      setTeacherOptions(teacherOptionsData)
    } catch (error) {
      console.error("Error fetching dropdown data:", error)
      toast.error("Failed to load dropdown data")
    }
  }

  const fetchCourses = async () => {
    try {
      setLoading(true)
      const response = await getAllCourses({
        searchText,
      })

      // Filter courses by class if class filter is set
      let filteredCourses = response.data;
      if (filters.class) {
        const selectedClass = classOptions.find(c => c.value === filters.class);
        // Note: Class filtering should be done via class.course array
        // For now, show all courses - class association is handled separately
      }

      setCourses(filteredCourses)
      // Simple courses API doesn't have pagination
      setPagination({
        currentPage: 1,
        totalPages: 1,
        total: filteredCourses.length,
        limit: filteredCourses.length,
      })
    } catch (error) {
      console.error("Error fetching courses:", error)
      toast.error("Failed to load courses")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDropdownData()
  }, [currentSession?._id])

  useEffect(() => {
    fetchCourses()
  }, [pagination.currentPage, filters, sortBy, searchText])

  useEffect(() => {
    if (classIdFromUrl) {
      setFilters(prev => ({
        ...prev,
        class: classIdFromUrl,
      }))
      setFormData(prev => ({
        ...prev,
        class: classIdFromUrl,
      }))
    }
  }, [classIdFromUrl])

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setLoading(true)
      await createCourse(formData)
      await fetchCourses()

      const modal = document.getElementById("add_subject")
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal
        )
        if (bootstrapModal) {
          bootstrapModal.hide()
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]'
          ) as HTMLElement
          if (closeButton) closeButton.click()
        }
      }

      setFormData({
        title: "",
        courseIdentifier: "",
      })

      toast.success("Course created successfully!")
    } catch (error: any) {
      console.error("Error creating course:", error)
      toast.error(error.response?.data?.error || "Failed to create course")
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedCourseId) {
      toast.error("No course selected for update")
      return
    }

    try {
      setLoading(true)
      await updateCourse(selectedCourseId, editFormData)
      await fetchCourses()

      const modal = document.getElementById("edit_subject")
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal
        )
        if (bootstrapModal) {
          bootstrapModal.hide()
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]'
          ) as HTMLElement
          if (closeButton) closeButton.click()
        }
      }

      setEditFormData({})
      setSelectedCourseId("")

      toast.success("Course updated successfully!")
    } catch (error: any) {
      console.error("Error updating course:", error)
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update course"
      )
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCourse = async () => {
    if (!selectedCourseId) return

    try {
      setLoading(true)
      await deleteCourse(selectedCourseId)
      await fetchCourses()
      setSelectedCourseId("")

      const modal = document.getElementById("delete-modal")
      if (modal) {
        const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance(
          modal
        )
        if (bootstrapModal) {
          bootstrapModal.hide()
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="modal"]'
          ) as HTMLElement
          if (closeButton) closeButton.click()
        }
      }

      toast.success("Course deleted successfully!")
    } catch (error: any) {
      console.error("Error deleting course:", error)
      toast.error(error.response?.data?.error || "Failed to delete course")
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  const handleSortSelect = (item: {
    label: string
    value: {
      sortBy: string
      sortOrder: string
    }
  }) => {
    setSortBy(item)
  }

  const prepareEditCourse = (course: Course) => {
    setSelectedCourseId(course._id)
    setEditFormData({
      title: course.title,
      courseIdentifier: course.courseIdentifier,
    })
  }

  const prepareDeleteCourse = (courseId: string) => {
    setSelectedCourseId(courseId)
  }

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
  }

  const handlePrint = useReactToPrint({ contentRef: componentRef })

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      courses.map(course => ({
        "Course ID": course.courseIdentifier || course._id,
        "Course Title": course.title || "N/A",
        "Created Date": course.createdAt ? new Date(course.createdAt).toLocaleDateString() : "N/A",
      }))
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Courses")
    XLSX.writeFile(wb, "courses.xlsx")
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "courseIdentifier",
      render: (text: string, record: Course) => (
        <Link to="#" className="link-primary">
          {record.courseIdentifier || record._id}
        </Link>
      ),
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
      render: (title: string) => title || "N/A",
    },
    {
      title: "Course Identifier",
      dataIndex: "courseIdentifier",
      sorter: true,
      render: (identifier: string) => identifier || "N/A",
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string, record: Course) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_subject"
                  onClick={() => prepareEditCourse(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete-modal"
                  onClick={() => prepareDeleteCourse(record._id)}
                >
                  <i className="ti ti-trash-x me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {currentClassName ? `${currentClassName} - Courses` : "Courses"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Academic </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {currentClassName
                      ? `${currentClassName} Courses`
                      : "All Courses"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchCourses}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_subject"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Course
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Courses List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                {currentClassName
                  ? `${currentClassName} - Courses`
                  : "All Courses"}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-12"></div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sectionOptions}
                                value={
                                  sectionOptions.find(
                                    option => option.value === filters.section
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={selectedOption =>
                                  handleFilterChange(
                                    "section",
                                    selectedOption?.value || ""
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <select
                                className="form-select"
                                value={filters.status}
                                onChange={e =>
                                  handleFilterChange("status", e.target.value)
                                }
                              >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={() =>
                            setFilters({
                              status: "",
                              class: classIdFromUrl || "",
                              section: "",
                              session: "",
                            })
                          }
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {CLASSES_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Class Subjects</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              {/* Courses List */}
              <Table
                columns={columns}
                dataSource={courses}
                loading={loading}
                setSearchText={setSearchText}
              />
            </div>
          </div>
          {/* /Courses List */}
        </div>
      </div>

      {/* Modals */}
      {/* Add Course Modal */}
      <div className="modal fade" id="add_subject">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Course - {currentClassName}</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateCourse}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Title <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title || ""}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Course Identifier <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.courseIdentifier || ""}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            courseIdentifier: e.target.value.toUpperCase(),
                          }))
                        }
                        placeholder="e.g., MATH-101, PHY-09"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Course"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Course Modal */}
      <div className="modal fade" id="edit_subject">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Course - {currentClassName}</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateCourse}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editFormData.name || ""}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form>
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash-x" />
                </span>
                <h4>Confirm Deletion</h4>
                <p>
                  You want to delete this course? This action cannot be undone.
                </p>
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteCourse}
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Yes, Delete"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClassSubject
